import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";

export default function FeesMasterTemplate() {
    const { http, user } = AuthUser();
    const [templates, setTemplates] = useState([]);
    const [feesTypes, setFeesTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [selectedItems, setSelectedItems] = useState({});

    useEffect(() => {
        fetchTemplates();
        fetchFeesTypes();
    }, []);

    const fetchTemplates = () => {
        setIsLoading(true);
        http.get(`/fees_master_templates?company_id=${user.company_id}`)
            .then((res) => { setTemplates(res.data.data || []); setIsLoading(false); })
            .catch(() => { setIsLoading(false); toast.error("Error fetching templates"); });
    };

    const fetchFeesTypes = () => {
        http.get(`/tution_feeslist?company_id=${user.company_id}`)
            .then((res) => setFeesTypes(res.data.data || []))
            .catch(() => {});
    };

    const handleCheckbox = (feesTypeId) => {
        setSelectedItems((prev) => {
            const copy = { ...prev };
            if (copy[feesTypeId] !== undefined) {
                delete copy[feesTypeId];
            } else {
                copy[feesTypeId] = "";
            }
            return copy;
        });
    };

    const handleAmountChange = (feesTypeId, value) => {
        setSelectedItems((prev) => ({ ...prev, [feesTypeId]: value }));
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setTemplateName("");
        setSelectedItems({});
    };

    const handleSubmit = () => {
        if (!templateName.trim()) { toast.error("Template name is required"); return; }
        const items = Object.entries(selectedItems)
            .filter(([_, amt]) => amt !== "" && parseFloat(amt) >= 0)
            .map(([feesTypeId, amount]) => ({ fees_type_id: parseInt(feesTypeId), amount: parseFloat(amount) }));

        if (items.length === 0) { toast.error("Select at least one fee type with amount"); return; }

        const payload = { name: templateName.trim(), company_id: user.company_id, items };

        const request = editingId
            ? http.put(`/fees_master_template/${editingId}`, payload)
            : http.post(`/fees_master_template`, payload);

        request
            .then(() => {
                toast.success(editingId ? "Template updated" : "Template created");
                resetForm();
                fetchTemplates();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Error saving template");
            });
    };

    const handleEdit = (template) => {
        setEditingId(template.id);
        setTemplateName(template.name);
        const items = {};
        (template.items || []).forEach((item) => {
            items[item.fees_type_id] = item.amount.toString();
        });
        setSelectedItems(items);
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (!window.confirm("Delete this template?")) return;
        http.delete(`/fees_master_template/${id}`)
            .then(() => { toast.success("Template deleted"); fetchTemplates(); })
            .catch(() => toast.error("Error deleting"));
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0 fw-bold">Fees Master Template</h5>
                    {!showForm && (
                        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
                            <i className="fas fa-plus me-1"></i>Add Template
                        </button>
                    )}
                </div>

                {showForm && (
                    <div className="card mb-3 shadow-sm">
                        <div className="card-body">
                            <h6 className="card-title fw-bold mb-3">{editingId ? "Edit Template" : "Create Template"}</h6>
                            <div className="row g-2 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Template Name *</label>
                                    <input type="text" className="form-control form-control-sm" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Enter template name" />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <small className="text-muted">Selected: <strong className="text-primary">{Object.keys(selectedItems).length}</strong></small>
                                </div>
                            </div>

                            <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Select Fee Types & Enter Amount</label>
                            <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                                <table className="table table-sm table-bordered" style={{ fontSize: "0.82rem" }}>
                                    <thead className="table-dark sticky-top">
                                        <tr>
                                            <th style={{ width: "50px" }} className="text-center">Select</th>
                                            <th>Fee Type Name</th>
                                            <th style={{ width: "160px" }}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feesTypes.map((ft) => (
                                            <tr key={ft.id} className={selectedItems[ft.id] !== undefined ? "table-success" : ""}>
                                                <td className="text-center">
                                                    <input type="checkbox" className="form-check-input" checked={selectedItems[ft.id] !== undefined} onChange={() => handleCheckbox(ft.id)} />
                                                </td>
                                                <td>{ft.name}</td>
                                                <td>
                                                    {selectedItems[ft.id] !== undefined && (
                                                        <input type="number" className="form-control form-control-sm" value={selectedItems[ft.id]} onChange={(e) => handleAmountChange(ft.id, e.target.value)} placeholder="0.00" min="0" step="0.01" />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {feesTypes.length === 0 && (
                                            <tr><td colSpan={3} className="text-center text-muted py-3">No fee types found. Please add fee types from Fees Type menu first.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button className="btn btn-success btn-sm px-3" onClick={handleSubmit}>
                                    <i className="fas fa-save me-1"></i>{editingId ? "Update" : "Save"}
                                </button>
                                <button className="btn btn-secondary btn-sm px-3" onClick={resetForm}>
                                    <i className="fas fa-times me-1"></i>Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
                ) : templates.length === 0 && !showForm ? (
                    <div className="alert alert-info">No templates found. Create one to get started.</div>
                ) : (
                    !showForm && templates.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover" style={{ fontSize: "0.83rem" }}>
                                <thead className="table-primary">
                                    <tr>
                                        <th style={{ width: "50px" }}>#</th>
                                        <th>Template Name</th>
                                        <th>Fee Types & Amounts</th>
                                        <th style={{ width: "120px" }}>Total</th>
                                        <th style={{ width: "110px" }}>Created</th>
                                        <th style={{ width: "100px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map((t, idx) => {
                                        const totalAmt = (t.items || []).reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
                                        return (
                                            <tr key={t.id}>
                                                <td>{idx + 1}</td>
                                                <td><strong>{t.name}</strong></td>
                                                <td>
                                                    {(t.items || []).map((item, i) => (
                                                        <span key={i} className="badge bg-light text-dark border me-1 mb-1" style={{ fontSize: "0.75rem" }}>
                                                            {item.fees_type ? item.fees_type.name : `ID:${item.fees_type_id}`} — <strong>{parseFloat(item.amount).toLocaleString()}</strong>
                                                        </span>
                                                    ))}
                                                </td>
                                                <td className="text-end"><strong>{totalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></td>
                                                <td>{moment(t.created_at).format("DD MMM YY")}</td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-primary me-1 px-2 py-0" style={{ fontSize: "0.72rem" }} onClick={() => handleEdit(t)} title="Edit">
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger px-2 py-0" style={{ fontSize: "0.72rem" }} onClick={() => handleDelete(t.id)} title="Delete">
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>
        </>
    );
}
